/**
 * Phase 7 - Profile Insights: External assessment result cards.
 *
 * Renders 4 cards (MBTI, 16Personalities, Love Language, Dosti Connection Style)
 * plus a preferred-personality-source picker.
 *
 * RULES ENFORCED HERE:
 *  - Never label self-reported results as "verified".
 *  - Never show provider logos - text labels only.
 *  - Consent toggles default OFF (server-enforced) and are user-configurable.
 *  - External "take-the-assessment" links open in a new tab; no data is
 *    appended to the URL.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Sparkles, Trash2, Save, Info, ExternalLink, Check, ChevronRight,
} from 'lucide-react';
import api from '@/utils/api';

const CARD_ORDER = [
  { id: 'myers_briggs',          label: 'Myers-Briggs Type Indicator',
    hint: 'A 4-letter personality code (e.g. INFJ, ESTP).' },
  { id: 'sixteen_personalities', label: '16Personalities Result',
    hint: 'A 4-letter code with an optional -A (Assertive) or -T (Turbulent) suffix.' },
  { id: 'five_love_languages',   label: 'Love Language',
    hint: 'Your primary way of expressing and receiving care.' },
  { id: 'dosti_connection_style',label: 'Dosti Connection Style',
    hint: 'Our first-party 5-minute survey. Original questions, no third party.' },
];

/* ------------------------------------------------------------- */
/*  Root - fetches everything and renders the four cards          */
/* ------------------------------------------------------------- */
const ProfileInsights = () => {
  const [providers, setProviders] = useState(null);
  const [externalLinks, setExternalLinks] = useState({});
  const [rows, setRows] = useState([]);
  const [preferred, setPreferred] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dostiOpen, setDostiOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const [meta, mine] = await Promise.all([
      api.get('/assessments/providers'),
      api.get('/profile/assessments'),
    ]);
    setProviders(meta.data.providers);
    setExternalLinks(meta.data.external_links || {});
    setRows(mine.data.results || []);
    setPreferred(mine.data.preferred_personality_source || null);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const byProvider = useMemo(() => {
    const m = {};
    for (const r of rows) m[r.provider] = r;
    return m;
  }, [rows]);

  const hasBothPersonality = !!byProvider.myers_briggs && !!byProvider.sixteen_personalities;

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="insights-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profile-insights-section">
      <Alert className="border-secondary-200 bg-secondary-50/50">
        <Info className="h-4 w-4 text-secondary-800" />
        <AlertDescription className="text-secondary-900">
          These results are self-reported. Dostination does not administer any
          third-party assessment or verify results with the provider. You control
          how each result is used, per assessment.
        </AlertDescription>
      </Alert>

      {hasBothPersonality && (
        <PreferredPersonalityPicker
          preferred={preferred}
          onChange={async (val) => {
            await api.patch('/profile/preferred-personality',
              { preferred_personality_source: val || null });
            setPreferred(val || null);
          }}
        />
      )}

      {CARD_ORDER.map(({ id, label, hint }) => {
        const row = byProvider[id];
        const link = externalLinks[id] || null;
        if (id === 'dosti_connection_style') {
          return (
            <DostiCard key={id} label={label} hint={hint} row={row}
                       onOpen={() => setDostiOpen(true)}
                       onDelete={async () => {
                         if (row?.id) {
                           await api.delete(`/profile/assessments/${row.id}`);
                           load();
                         }
                       }}
                       onConsent={async (patch) => {
                         if (row?.id) {
                           await api.patch(`/profile/assessments/${row.id}/consents`, patch);
                           load();
                         }
                       }}
            />
          );
        }
        return (
          <ExternalResultCard
            key={id}
            providerId={id}
            label={label}
            hint={hint}
            row={row}
            externalLink={link}
            onSaved={load}
          />
        );
      })}

      <DostiSurveyModal
        open={dostiOpen}
        onOpenChange={setDostiOpen}
        existing={byProvider.dosti_connection_style || null}
        onSubmitted={() => { setDostiOpen(false); load(); }}
      />
    </div>
  );
};

/* ------------------------------------------------------------- */
/*  Preferred personality picker (MBTI vs 16P)                    */
/* ------------------------------------------------------------- */
const PreferredPersonalityPicker = ({ preferred, onChange }) => (
  <Card className="border-secondary-200 shadow-soft" data-testid="preferred-personality-picker">
    <CardContent className="p-5 space-y-3">
      <div className="flex items-start gap-2">
        <Sparkles className="h-5 w-5 text-secondary-700 mt-0.5" />
        <div className="flex-1">
          <div className="font-display text-lg font-bold text-primary-900">
            Preferred personality source
          </div>
          <p className="text-sm text-neutral-600">
            You have both MBTI and 16Personalities on file. Pick which one to use
            for curated seating and introductions so we do not double-count.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {[
          { v: 'myers_briggs',          label: 'Use my MBTI result' },
          { v: 'sixteen_personalities', label: 'Use my 16Personalities result' },
        ].map(({ v, label }) => (
          <button
            key={v}
            data-testid={`preferred-${v}`}
            onClick={() => onChange(v)}
            className={`text-left border rounded-lg p-3 text-sm transition ${
              preferred === v
                ? 'border-secondary-500 bg-secondary-50 text-primary-900'
                : 'border-neutral-200 hover:border-secondary-300'
            }`}
          >
            <div className="flex items-center gap-2">
              {preferred === v && <Check className="h-4 w-4 text-secondary-700" />}
              <span>{label}</span>
            </div>
          </button>
        ))}
      </div>
      {preferred && (
        <button
          onClick={() => onChange(null)}
          className="text-xs text-neutral-500 hover:text-neutral-700 underline"
          data-testid="clear-preferred-personality"
        >
          Clear preference (default to most recent)
        </button>
      )}
    </CardContent>
  </Card>
);

/* ------------------------------------------------------------- */
/*  External result card (MBTI / 16P / Love Language)             */
/* ------------------------------------------------------------- */
const MBTI_TYPES = ['ISTJ','ISFJ','INFJ','INTJ','ISTP','ISFP','INFP','INTP',
                    'ESTP','ESFP','ENFP','ENTP','ESTJ','ESFJ','ENFJ','ENTJ'];
const LOVE_LANGUAGES = ['Words of Affirmation','Quality Time','Acts of Service',
                        'Receiving Gifts','Physical Touch'];

const ExternalResultCard = ({ providerId, label, hint, row, externalLink, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => hydrate(row, providerId));
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(hydrate(row, providerId)); }, [row, providerId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post('/profile/assessments', {
        provider: providerId,
        result_value: form.result_value,
        date_taken: form.date_taken || null,
        source_url: form.source_url || null,
        notes: form.notes || null,
        full_result_text: (form.full_result_text || '').trim() || null,
        screenshot_media_ids: form.screenshot_media_ids || [],
        user_confirmed_accurate: !!form.user_confirmed_accurate,
        consent_use_for_seating: !!form.consent_use_for_seating,
        consent_use_for_introductions: !!form.consent_use_for_introductions,
        consent_display_on_profile: !!form.consent_display_on_profile,
        consent_use_for_ai_context: !!form.consent_use_for_ai_context,
      });
      setEditing(false);
      onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!row?.id) return;
    if (!window.confirm('Remove this result from your profile?')) return;
    await api.delete(`/profile/assessments/${row.id}`);
    onSaved();
  };

  const updateConsent = async (patch) => {
    if (!row?.id) { setForm({ ...form, ...patch }); return; }
    await api.patch(`/profile/assessments/${row.id}/consents`, patch);
    onSaved();
  };

  return (
    <Card className="border shadow-soft" data-testid={`assessment-card-${providerId}`}>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display text-lg font-bold text-primary-900">{label}</div>
            <p className="text-xs text-neutral-500 mt-0.5">{hint}</p>
          </div>
          {externalLink && (
            <a href={externalLink} target="_blank" rel="noopener noreferrer"
               className="text-xs text-secondary-800 hover:underline inline-flex items-center gap-1"
               data-testid={`external-link-${providerId}`}>
              Take the assessment <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>

        {row && !editing ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="text-2xl font-display font-bold text-primary-900"
                   data-testid={`result-value-${providerId}`}>
                {row.result_value}
              </div>
              <span className="text-[10px] uppercase tracking-widest bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded">
                Self-reported
              </span>
            </div>
            {row.date_taken && (
              <div className="text-xs text-neutral-500">Taken: {row.date_taken}</div>
            )}
            {row.notes && (
              <div className="text-sm text-neutral-700 whitespace-pre-wrap">{row.notes}</div>
            )}

            <ConsentToggles row={row} onChange={updateConsent} providerId={providerId} />

            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setEditing(true)}
                      data-testid={`edit-${providerId}`}>Edit</Button>
              <Button variant="outline" onClick={del}
                      className="text-accent-700 hover:bg-accent-50"
                      data-testid={`delete-${providerId}`}>
                <Trash2 className="h-4 w-4 mr-1" /> Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid={`edit-form-${providerId}`}>
            <ResultField providerId={providerId} value={form.result_value}
                         onChange={(v) => setForm({ ...form, result_value: v })} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-secondary-800">Date taken (optional)</Label>
                <Input type="date" value={form.date_taken || ''}
                       onChange={(e) => setForm({ ...form, date_taken: e.target.value })}
                       data-testid={`date-taken-${providerId}`} />
              </div>
              <div>
                <Label className="text-xs text-secondary-800">Source URL (optional)</Label>
                <Input value={form.source_url || ''}
                       placeholder="https://..."
                       onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                       data-testid={`source-url-${providerId}`} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-secondary-800">Notes (optional)</Label>
              <Textarea rows={2} value={form.notes || ''}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        data-testid={`notes-${providerId}`} />
            </div>
            <FullResultTextField
              providerId={providerId}
              value={form.full_result_text || ''}
              onChange={(v) => setForm({ ...form, full_result_text: v })}
            />
            <AssessmentScreenshotGrid
              providerId={providerId}
              mediaIds={form.screenshot_media_ids || []}
              onChange={(ids) => setForm({ ...form, screenshot_media_ids: ids })}
            />
            <AiContextConsent
              providerId={providerId}
              checked={!!form.consent_use_for_ai_context}
              onChange={(v) => setForm({ ...form, consent_use_for_ai_context: v })}
            />
            <ConsentToggles row={form} onChange={(p) => setForm({ ...form, ...p })}
                            providerId={providerId} inline />
            <div className="flex gap-2 pt-1">
              <Button onClick={save} disabled={busy || !form.result_value}
                      className="bg-primary-800 hover:bg-primary-900 text-white"
                      data-testid={`save-${providerId}`}>
                <Save className="h-4 w-4 mr-1" /> {busy ? 'Saving...' : 'Save result'}
              </Button>
              {row && (
                <Button variant="outline" onClick={() => { setEditing(false); setForm(hydrate(row, providerId)); }}
                        data-testid={`cancel-${providerId}`}>Cancel</Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const hydrate = (row, providerId) => {
  const base = {
    result_value: '',
    date_taken: '',
    source_url: '',
    notes: '',
    full_result_text: '',
    screenshot_media_ids: [],
    user_confirmed_accurate: false,
    consent_use_for_seating: false,
    consent_use_for_introductions: false,
    consent_display_on_profile: false,
    consent_use_for_ai_context: false,
  };
  if (!row) return base;
  // Back-compat: legacy singular `screenshot_media_id` becomes a 1-item list
  // if the plural field isn't set yet.
  const ids = Array.isArray(row.screenshot_media_ids) && row.screenshot_media_ids.length
    ? row.screenshot_media_ids
    : (row.screenshot_media_id ? [row.screenshot_media_id] : []);
  return {
    ...base,
    result_value: row.result_value || '',
    date_taken: row.date_taken || '',
    source_url: row.source_url || '',
    notes: row.notes || '',
    full_result_text: row.full_result_text || '',
    screenshot_media_ids: ids,
    user_confirmed_accurate: !!row.user_confirmed_accurate,
    consent_use_for_seating: !!row.consent_use_for_seating,
    consent_use_for_introductions: !!row.consent_use_for_introductions,
    consent_display_on_profile: !!row.consent_display_on_profile,
    consent_use_for_ai_context: !!row.consent_use_for_ai_context,
  };
};

const FULL_RESULT_TEXT_MAX = 50_000;

const FullResultTextField = ({ providerId, value, onChange }) => {
  const len = (value || '').length;
  const over = len > FULL_RESULT_TEXT_MAX;
  return (
    <div>
      <Label className="text-xs text-secondary-800">
        Paste your full result text (optional)
      </Label>
      <Textarea
        rows={8}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Paste the full result page copy from your assessment provider. Dost can use this to match your tone when polishing your writing."
        data-testid={`assessment-full-result-textarea-${providerId}`}
      />
      <div className={`mt-1 text-[11px] ${over ? 'text-accent-700' : 'text-neutral-500'}`}>
        {len.toLocaleString()} / {FULL_RESULT_TEXT_MAX.toLocaleString()} characters
        {over ? ' (please trim before saving)' : ''}
      </div>
    </div>
  );
};

const ASSESSMENT_SCREENSHOT_MAX = 6;

const AssessmentScreenshotGrid = ({ providerId, mediaIds, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [urls, setUrls] = useState([]);
  const [err, setErr] = useState('');
  const [broken, setBroken] = useState({});

  // Rehydrate signed URLs whenever the id list changes. The upload
  // response returns a `url` we can use directly; on reload from DB we
  // only have ids so we hit the media endpoint per id.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const out = [];
      for (const id of mediaIds) {
        // The initial upload cached the signed URL on the id; store it in
        // a Map on the id so we don't re-fetch. If we only have the id
        // (page reload), just point at the raw media endpoint; the browser
        // will send the session cookie and get a signed redirect.
        out.push({ id, url: `/api/media/${id}` });
      }
      if (!cancelled) setUrls(out);
    };
    load();
    return () => { cancelled = true; };
  }, [mediaIds.join(',')]);

  const upload = async (files) => {
    setErr('');
    setUploading(true);
    const next = [...mediaIds];
    for (const file of files) {
      if (next.length >= ASSESSMENT_SCREENSHOT_MAX) break;
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('owner_type', 'assessment_screenshot');
        fd.append('is_public', 'false');
        const { data } = await api.post('/media/upload/v2', fd);
        if (data.id) next.push(data.id);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Upload failed');
      }
    }
    onChange(next);
    setUploading(false);
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, ASSESSMENT_SCREENSHOT_MAX - mediaIds.length);
    e.target.value = '';
    if (!files.length) return;
    await upload(files);
  };

  const removeAt = (idx) => {
    onChange(mediaIds.filter((_, i) => i !== idx));
  };

  const canAddMore = mediaIds.length < ASSESSMENT_SCREENSHOT_MAX;

  return (
    <div>
      <Label className="text-xs text-secondary-800">
        Screenshots (optional, up to {ASSESSMENT_SCREENSHOT_MAX})
      </Label>
      <div
        className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-1"
        data-testid={`assessment-screenshot-grid-${providerId}`}
      >
        {urls.map((entry, i) => (
          <div key={`${entry.id}-${i}`} className="relative aspect-square rounded-xl overflow-hidden border border-neutral-200 bg-neutral-50">
            {broken[i] ? (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-secondary-100 via-primary-50 to-neutral-100 text-secondary-800/80 font-semibold text-xs">
                Screenshot
              </div>
            ) : (
              <img
                data-testid={`assessment-screenshot-${providerId}-${i}`}
                src={entry.url}
                alt={`screenshot-${i}`}
                loading="lazy"
                onError={() => setBroken((s) => ({ ...s, [i]: true }))}
                className="w-full h-full object-cover"
              />
            )}
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-full p-1 shadow-sm"
              aria-label="Remove"
              data-testid={`assessment-screenshot-remove-${providerId}-${i}`}
            >
              <Trash2 className="h-3 w-3 text-accent-700" />
            </button>
          </div>
        ))}
        {canAddMore && (
          <label
            className={`aspect-square rounded-xl border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center text-neutral-500 text-xs cursor-pointer hover:border-secondary-400 hover:text-secondary-700 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            data-testid={`assessment-screenshot-upload-tile-${providerId}`}
          >
            <span className="text-lg leading-none mb-1">+</span>
            <span>{uploading ? 'Uploading' : 'Add screenshot'}</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={onFile} />
          </label>
        )}
      </div>
      {err && <div className="text-xs text-accent-700 mt-2" data-testid={`assessment-screenshot-error-${providerId}`}>{err}</div>}
    </div>
  );
};

const AiContextConsent = ({ providerId, checked, onChange }) => (
  <div className="flex items-start justify-between gap-3 py-2 border-t border-neutral-100">
    <div className="pr-3">
      <div className="text-sm font-semibold text-primary-900">
        Let Dost read this when polishing my writing
      </div>
      <div className="text-xs text-neutral-500 mt-0.5">
        Your assessment details help Dost match tone. Off by default.
      </div>
    </div>
    <Switch
      checked={!!checked}
      onCheckedChange={onChange}
      data-testid={`assessment-ai-consent-toggle-${providerId}`}
    />
  </div>
);

const ResultField = ({ providerId, value, onChange }) => {
  if (providerId === 'myers_briggs') {
    return (
      <div>
        <Label className="text-xs text-secondary-800">Your MBTI type</Label>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger data-testid="mbti-select"><SelectValue placeholder="Select a type or 'Prefer not to answer'" /></SelectTrigger>
          <SelectContent>
            {MBTI_TYPES.map((t) => (
              <SelectItem key={t} value={t} data-testid={`mbti-opt-${t}`}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (providerId === 'sixteen_personalities') {
    return (
      <div>
        <Label className="text-xs text-secondary-800">
          16Personalities result (4-letter code, optional -A or -T)
        </Label>
        <Input value={value} onChange={(e) => onChange(e.target.value.toUpperCase())}
               placeholder="e.g. INFJ-A"
               data-testid="sixteenp-input" />
      </div>
    );
  }
  if (providerId === 'five_love_languages') {
    return (
      <div>
        <Label className="text-xs text-secondary-800">Primary love language</Label>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger data-testid="lovelang-select"><SelectValue placeholder="Choose your primary love language" /></SelectTrigger>
          <SelectContent>
            {LOVE_LANGUAGES.map((t) => (
              <SelectItem key={t} value={t} data-testid={`ll-opt-${t.replace(/\s+/g,'-')}`}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  return null;
};

const ConsentToggles = ({ row, onChange, providerId, inline = false }) => {
  const items = [
    { k: 'consent_use_for_seating',        label: 'Use for curated seating at events' },
    { k: 'consent_use_for_introductions',  label: 'Use for organizer introductions' },
    { k: 'consent_display_on_profile',     label: 'Display on my public event profile' },
    { k: 'user_confirmed_accurate',        label: 'I confirm this result is accurate' },
  ];
  return (
    <div className={`space-y-2 ${inline ? '' : 'pt-2 border-t border-neutral-100'}`}
         data-testid={`consents-${providerId}`}>
      {!inline && (
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
          How this result is used
        </div>
      )}
      {items.map(({ k, label }) => (
        <div key={k} className="flex items-center justify-between gap-3 text-sm">
          <span className="text-neutral-700">{label}</span>
          <Switch
            checked={!!row[k]}
            onCheckedChange={(v) => onChange({ [k]: v })}
            data-testid={`consent-${providerId}-${k}`}
          />
        </div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------- */
/*  Dosti Connection Style CARD (first-party)                     */
/* ------------------------------------------------------------- */
const DostiCard = ({ label, hint, row, onOpen, onDelete, onConsent }) => (
  <Card className="border shadow-soft" data-testid="assessment-card-dosti_connection_style">
    <CardContent className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display text-lg font-bold text-primary-900">{label}</div>
          <p className="text-xs text-neutral-500 mt-0.5">{hint}</p>
        </div>
        <span className="text-[10px] uppercase tracking-widest bg-secondary-100 text-secondary-800 px-2 py-0.5 rounded">
          First-party
        </span>
      </div>

      {row ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(row.dimension_scores || {}).map(([dim, s]) => (
              <div key={dim} data-testid={`dim-${dim}`}>
                <div className="flex items-center justify-between text-xs text-neutral-600 mb-0.5">
                  <span>{s.dimension_label}</span>
                  <span className="font-semibold text-primary-900">{s.label}</span>
                </div>
                <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-secondary-400 to-secondary-600"
                       style={{ width: `${s.score_0_100}%` }} />
                </div>
              </div>
            ))}
          </div>
          <ConsentToggles row={row} onChange={onConsent}
                          providerId="dosti_connection_style" />
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onOpen}
                    data-testid="retake-dosti-btn">Retake survey</Button>
            <Button variant="outline" onClick={onDelete}
                    className="text-accent-700 hover:bg-accent-50"
                    data-testid="delete-dosti-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Remove
            </Button>
          </div>
        </>
      ) : (
        <Button onClick={onOpen}
                className="bg-primary-800 hover:bg-primary-900 text-white"
                data-testid="start-dosti-btn">
          Take the 5-minute Dosti Connection Style survey
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      )}
    </CardContent>
  </Card>
);

/* ------------------------------------------------------------- */
/*  Dosti Survey Modal (15 questions, 5-point Likert)             */
/* ------------------------------------------------------------- */
const DostiSurveyModal = ({ open, onOpenChange, existing, onSubmitted }) => {
  const [def, setDef] = useState(null);
  const [answers, setAnswers] = useState({});
  const [consents, setConsents] = useState({
    consent_use_for_seating: !!existing?.consent_use_for_seating,
    consent_use_for_introductions: !!existing?.consent_use_for_introductions,
    consent_display_on_profile: !!existing?.consent_display_on_profile,
    user_confirmed_accurate: !!existing?.user_confirmed_accurate,
  });
  const [step, setStep] = useState(0); // 0..14 questions, 15 = consent + submit
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await api.get('/dosti-connection-style/definition');
      setDef(data);
      setAnswers(existing?.answers_snapshot || {});
      setStep(0);
    })();
  }, [open, existing]);

  if (!open || !def) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" />
      </Dialog>
    );
  }

  const total = def.questions.length;
  const q = step < total ? def.questions[step] : null;
  const answered = Object.keys(answers).filter((k) => def.questions.find((qq) => qq.id === k)).length;
  const progress = Math.round((Math.min(step, total) / (total + 1)) * 100);

  const setAnswer = (val) => setAnswers({ ...answers, [q.id]: val });

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/dosti-connection-style/submit', { answers, ...consents });
      onSubmitted();
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dosti-survey-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Dosti Connection Style</DialogTitle>
        </DialogHeader>

        <div className="h-1 bg-neutral-100 rounded overflow-hidden">
          <div className="h-full bg-secondary-500" style={{ width: `${progress}%` }} />
        </div>

        {q ? (
          <div className="space-y-4" data-testid={`dosti-q-${q.id}`}>
            <div className="text-xs uppercase tracking-widest text-secondary-800">
              Question {step + 1} of {total}
            </div>
            <div className="text-lg font-display text-primary-900">{q.text}</div>
            <div className="grid grid-cols-1 gap-2">
              {[1, 2, 3, 4, 5].map((v) => (
                <button
                  key={v}
                  data-testid={`dosti-q-${q.id}-opt-${v}`}
                  onClick={() => setAnswer(v)}
                  className={`text-left border rounded-lg p-3 text-sm transition ${
                    answers[q.id] === v
                      ? 'border-secondary-500 bg-secondary-50 text-primary-900'
                      : 'border-neutral-200 hover:border-secondary-300'
                  }`}
                >
                  <span className="font-semibold mr-2">{v}.</span>
                  {def.likert_labels[v]}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between pt-1">
              <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))}
                      disabled={step === 0} data-testid="dosti-back">Back</Button>
              <div className="text-xs text-neutral-500">
                {answered}/{total} answered
              </div>
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!answers[q.id]}
                data-testid="dosti-next"
                className="bg-primary-800 hover:bg-primary-900 text-white"
              >
                {step === total - 1 ? 'Review' : 'Next'}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="dosti-consent-step">
            <div className="text-sm text-neutral-700">
              Ready to submit? Choose how you want us to use this result. You can
              change these later in the card on your profile.
            </div>
            <div className="space-y-2 text-sm">
              {[
                ['consent_use_for_seating',        'Use for curated seating at events'],
                ['consent_use_for_introductions',  'Use for organizer introductions'],
                ['consent_display_on_profile',     'Display on my public event profile'],
                ['user_confirmed_accurate',        'I confirm this result reflects me'],
              ].map(([k, label]) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <span className="text-neutral-700">{label}</span>
                  <Switch
                    checked={!!consents[k]}
                    onCheckedChange={(v) => setConsents({ ...consents, [k]: v })}
                    data-testid={`dosti-consent-${k}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between pt-1">
              <Button variant="outline" onClick={() => setStep(total - 1)}
                      data-testid="dosti-consent-back">Back</Button>
              <Button
                onClick={submit}
                disabled={busy || answered < total}
                data-testid="dosti-submit"
                className="bg-primary-800 hover:bg-primary-900 text-white"
              >
                {busy ? 'Submitting...' : 'Submit result'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileInsights;
